// SpinalOrthotics.js
import React from 'react';
import './spinal-orthotics.css';
import { Link } from "react-router-dom";

function SpinalOrthotics() {
  const orthoticTypes = [
    {
      id: "cervical",
      title: "Cervical Orthotics",
      description: "Specialized supports for the neck and upper spine, providing stability and pain relief for conditions like whiplash, cervical fractures, and degenerative disc disease.",
      features: ["Adjustable height options", "Breathable materials", "Custom molding available"]
    },
    {
      id: "thoracic",
      title: "Thoracic Orthotics",
      description: "Designed to support the mid-back region, these orthotics help manage conditions such as kyphosis, scoliosis, and compression fractures.",
      features: ["Lightweight carbon fiber", "Posture correction", "Pressure distribution pads"]
    },
    {
      id: "lumbar",
      title: "Lumbar Orthotics",
      description: "Comprehensive support for the lower back, addressing herniated discs, spinal stenosis, and post-surgical recovery needs.",
      features: ["Adjustable tension straps", "Heat retention panels", "Flexible stays"]
    },
    {
      id: "full-spine",
      title: "Full Spine Orthotics",
      description: "Complete spinal support systems for complex conditions requiring stabilization from the neck to the lower back.",
      features: ["Modular design", "Custom fit", "Maximum immobilization"]
    }
  ];

  const benefits = [
    {
      id: "pain-relief",
      title: "Effective Pain Relief",
      description: "Reduce spinal pain through proper alignment and support, allowing natural healing and improved daily comfort."
    },
    {
      id: "posture",
      title: "Improved Posture",
      description: "Correct and maintain healthy spinal alignment, preventing long-term complications and muscle strain."
    },
    {
      id: "mobility",
      title: "Enhanced Mobility",
      description: "Gain confidence in movement with orthotics designed to support your spine while allowing functional activity."
    },
    {
      id: "recovery",
      title: "Faster Recovery",
      description: "Accelerate healing from spinal injuries or surgeries with proper immobilization and support systems."
    }
  ];

  const processSteps = [
    {
      id: "step1",
      number: "01",
      title: "Clinical Assessment",
      description: "Thorough evaluation by our spinal specialists, including range of motion testing and medical history review."
    },
    {
      id: "step2",
      number: "02",
      title: "Body Scanning",
      description: "imaging to capture precise body contours for optimal fit and pressure distribution."
    },
    {
      id: "step3",
      number: "03",
      title: "Custom Fabrication",
      description: "Handcrafted orthotics using premium materials like carbon fiber, thermoplastics, and breathable padding."
    },
    {
      id: "step4",
      number: "04",
      title: "Fitting & Education",
      description: "Professional fitting with guidance on proper usage, care, and adjustment techniques."
    }
  ];

  const conditions = [
    { id: "scoliosis", name: "Scoliosis" },
    { id: "kyphosis", name: "Kyphosis" },
    { id: "herniated-disc", name: "Herniated Disc" },
    { id: "spinal-stenosis", name: "Spinal Stenosis" },
    { id: "fractures", name: "Vertebral Fractures" },
    { id: "post-surgery", name: "Post-Surgical Support" }
  ];

  return (
    <div className="spinal-orthotics-page">

      {/* Hero Section */}
      <section className="so-hero-section">
        <div className="so-hero-overlay">
          <div className="so-hero-container">
            <div className="so-hero-content">
              <span className="so-hero-badge">Spinal Orthotics</span>
              <h1 className="so-hero-title">
                Advanced Support for <span className="so-hero-highlight">Spinal Health</span>
              </h1>
              <p className="so-hero-subtitle">
                Custom-engineered spinal orthotics designed to provide optimal support, correct alignment, 
                and promote natural healing for a pain-free, active lifestyle.
              </p>
              <div className="so-hero-buttons">
                <Link to="/contactus" className="so-btn so-btn-primary">Schedule Consultation</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="so-intro-section">
        <div className="so-container">
          <div className="so-intro-grid">
            <div className="so-intro-content">
              <span className="so-section-badge">Overview</span>
              <h2 className="so-intro-heading">Comprehensive Spinal Orthotic Solutions</h2>
              <p className="so-intro-text">
                At Southern Rehab Care, we understand that spinal conditions require specialized, 
                personalized care. Our spinal orthotics are meticulously crafted to provide the 
                perfect balance of support, comfort, and functionality for your unique condition.
              </p>
              <p className="so-intro-text">
                Using advanced materials like carbon fiber, lightweight thermoplastics, and 
                pressure-relieving foams, we create orthotic devices that promote proper spinal 
                alignment while fitting seamlessly into your daily life.
              </p>
              <div className="so-intro-stats">
                <div className="so-intro-stat">
                  <span className="so-intro-stat-number">300+</span>
                  <span className="so-intro-stat-label">Patients Treated</span>
                </div>
                <div className="so-intro-stat">
                  <span className="so-intro-stat-number">98%</span>
                  <span className="so-intro-stat-label">Success Rate</span>
                </div>
                <div className="so-intro-stat">
                  <span className="so-intro-stat-number">15+</span>
                  <span className="so-intro-stat-label">Years Experience</span>
                </div>
              </div>
            </div>
            <div className="so-intro-image-wrapper">
              <img src="/Scoliosis.png" alt="Spinal-Scoliosis" className="so-intro-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Orthotic Types Section */}
      <section className="so-types-section">
        <div className="so-container">
          <div className="so-section-header">
            <span className="so-section-badge">Our Solutions</span>
<h2 className="so-section-heading">Get Healed with Quality Spinal Orthotics</h2>
<p className="so-section-subheading">Customized support for every region of the spine</p>
          </div>

<section>
  <div className='Scoliosis-prosthetic-gallery'>
    <img src='/upperf1.jpg' className='Scoliosis-orthotics-img' />
    <img src='/upperf3.jpg' className='Scoliosis-orthotics-img' />
    <img src='/upperf4.jpg' className='Scoliosis-orthotics-img' />
  </div>
</section>


          <div className="so-types-grid">
            {orthoticTypes.map((type) => (
              <div key={type.id} className="so-type-card">
                <h3 className="so-type-title">{type.title}</h3>
                <p className="so-type-description">{type.description}</p>
                <ul className="so-type-features">
                  {type.features.map((feature, index) => (
                    <li key={index} className="so-type-feature">
                      <span className="so-type-feature-dot"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="so-benefits-section">
        <div className="so-container">
          <div className="so-section-header">
            <span className="so-section-badge so-section-badge-light">Why Choose Us</span>
            <h2 className="so-section-heading so-section-heading-light">Benefits of Our Spinal Orthotics</h2>
            <p className="so-section-subheading so-section-subheading-light">Engineered for your healing and comfort</p>
          </div>
          <div className="so-benefits-grid">
            {benefits.map((benefit) => (
              <div key={benefit.id} className="so-benefit-card">
                <h3 className="so-benefit-title">{benefit.title}</h3>
                <p className="so-benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="so-process-section">
        <div className="so-container">
          <div className="so-section-header">
            <span className="so-section-badge">Our Process</span>
            <h2 className="so-section-heading">Your Journey to Spinal Health</h2>
            <p className="so-section-subheading">A step-by-step approach to your custom orthotic</p>
          </div>
          <div className="so-process-grid">
            {processSteps.map((step) => (
              <div key={step.id} className="so-process-card">
                <div className="so-process-number">{step.number}</div>
                <h3 className="so-process-title">{step.title}</h3>
                <p className="so-process-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions Section */}
      <section className="so-conditions-section">
        <div className="so-container">
          <div className="so-conditions-grid">
            <div className="so-conditions-content">
              <span className="so-conditions-badge">Conditions We Treat</span>
              <h2 className="so-conditions-heading">Spinal Conditions Managed with Orthotics</h2>
              <p className="so-conditions-text">
                Our custom spinal orthotics are effective for a wide range of conditions, from 
                degenerative diseases to traumatic injuries. We work closely with your physician 
                to develop the optimal treatment plan.
              </p>
              <div className="so-conditions-tags">
                {conditions.map((condition) => (
                  <span key={condition.id} className="so-conditions-tag">{condition.name}</span>
                ))}
              </div>
            </div>
            <div className="so-conditions-image-wrapper">
              <img src="/scoliosis_5.jpg" alt="Spinal Conditions" className="so-conditions-image" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="so-faq-section">
        <div className="so-container">
          <div className="so-section-header">
            <span className="so-section-badge">FAQ</span>
            <h2 className="so-section-heading">Frequently Asked Questions</h2>
            <p className="so-section-subheading">Common questions about spinal orthotics</p>
          </div>
          <div className="so-faq-grid">
            <div className="so-faq-item">
              <h3 className="so-faq-question">How long does it take to get a custom spinal orthotic?</h3>
              <p className="so-faq-answer">The entire process from initial consultation to final fitting typically takes 2-4 weeks, depending on the complexity of your condition and custom requirements.</p>
            </div>
            <div className="so-faq-item">
              <h3 className="so-faq-question">Will insurance cover my spinal orthotic?</h3>
              <p className="so-faq-answer">Most insurance plans, including Medicare and private insurers, provide coverage for medically necessary spinal orthotics. Our team will help verify your benefits.</p>
            </div>
            <div className="so-faq-item">
              <h3 className="so-faq-question">How long will my spinal orthotic last?</h3>
              <p className="so-faq-answer">With proper care, a spinal orthotic typically lasts 3-5 years. Regular follow-ups ensure optimal fit and function as your condition changes.</p>
            </div>
            <div className="so-faq-item">
              <h3 className="so-faq-question">Can I wear a spinal orthotic during physical activities?</h3>
              <p className="so-faq-answer">Yes! We offer activity-specific designs and materials that allow for safe participation in various physical activities while maintaining spinal support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="so-cta-section">
        <div className="so-container">
          <div className="so-cta-content">
            <h2 className="so-cta-heading">Ready to Find Relief?</h2>
            <p className="so-cta-subheading">Schedule a consultation with our spinal orthotic specialists today</p>
            <div className="so-cta-buttons">
              <Link to="/contactus" className="so-btn so-btn-primary">Contact Us</Link>
              <Link to="/contactus" className="so-btn so-btn-secondary-light">Book Appointment</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default SpinalOrthotics;